import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";
import {
  User,
  Phone,
  Lock,
  Eye,
  EyeOff,
  Home,
  MapPin,
  Building2,
  Hash,
  ChevronDown,
  ArrowRight,
} from "lucide-react";
import applogo from "../../assets/Images/applogo.png";
import {
  sendRegistrationOtpThunk,
  verifyRegistrationThunk,
} from "../../redux/thunk";
import { isAuthenticated } from "../../utils/useSession";
import { SPECIALIZATION_DETAILS } from "../../constants/specializations";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";

const FEE_OPTIONS = [
  "0",
  "299",
  "499",
  "999",
  "1199",
  "1499",
  "1999",
  "2499",
  "2999",
];

export default function Register() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isAuthenticated()) navigate("/home", { replace: true });
  }, [navigate]);

  // Form fields
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [subscriptionFee, setSubscriptionFee] = useState("");
  const [clinicAddress, setClinicAddress] = useState("");
  const [area, setArea] = useState("");
  const [city, setCity] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [amCode, setAmCode] = useState("");
  const [agreed, setAgreed] = useState(false);

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const otpRefs = [useRef(), useRef(), useRef(), useRef()];

  // UI state
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSpec, setShowSpec] = useState(false);
  const [showFee, setShowFee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!fullName.trim()) errs.fullName = "Full name is required";
    if (mobile.length !== 10) errs.mobile = "Enter a valid 10-digit number";
    if (!specialization) errs.specialization = "Select a specialization";
    if (!subscriptionFee) errs.subscriptionFee = "Select a subscription fee";
    if (!clinicAddress.trim())
      errs.clinicAddress = "Clinic address is required";
    if (!city.trim()) errs.city = "City is required";
    if (password.length < 8) errs.password = "Minimum 8 characters";
    if (password !== confirmPassword)
      errs.confirmPassword = "Passwords do not match";
    if (!agreed) errs.agreed = "You must agree to the Terms & Conditions";
    return errs;
  };

  const handleSendOtp = async () => {
    setError("");
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    try {
      setLoading(true);
      // The backend captures the full registration record here — userData saved
      // against this OTP request — not at final submit; /register only reads
      // requestId/otp (+ an optional subscriptionFee override) and pulls the rest
      // back out of that stored userData.
      const res = await dispatch(
        sendRegistrationOtpThunk({
          mobileNumber: `+91${mobile}`,
          displayName: fullName.trim(),
          specialization,
          subscriptionFee: Number(subscriptionFee),
          address: {
            clinicAddress: clinicAddress.trim(),
            areaSector: area.trim(),
            city: city.trim(),
          },
          password,
          accountManagerCode: amCode.trim(),
        })
      );
      if (res?.payload?.success) {
        setRequestId(res.payload.data?.requestId || "");
        setOtpSent(true);
      } else {
        setError(
          res?.payload?.message ||
            res?.error?.message ||
            "Failed to send OTP. Please try again."
        );
      }
    } catch (err) {
      setError(err?.message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setError("");
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 4) {
      setError("Please enter the 4-digit OTP.");
      return;
    }
    try {
      setLoading(true);
      const payload = {
        mobileNumber: `+91${mobile}`,
        displayName: fullName.trim(),
        specialization,
        subscriptionFee: Number(subscriptionFee),
        clinicAddress: clinicAddress.trim(),
        area: area.trim(),
        city: city.trim(),
        password,
        accountManagerCode: amCode.trim(),
        otp: enteredOtp,
        requestId,
      };
      const res = await dispatch(verifyRegistrationThunk(payload));
      if (res?.payload?.success) {
        navigate("/home", { replace: true });
      } else {
        setError(
          res?.payload?.message ||
            res?.error?.message ||
            "Registration failed. Please check the OTP and try again."
        );
      }
    } catch (err) {
      setError(err?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, val) => {
    const digit = val.replace(/\D/, "").slice(0, 1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    if (digit && index < 3) otpRefs[index + 1].current?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      otpRefs[index - 1].current?.focus();
  };

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel — same style as Login */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-gradient-to-br from-brand-600 via-brand-700 to-brand-900 p-12 relative overflow-hidden">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/5" />
        <div className="absolute -bottom-32 -right-24 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full bg-white/5" />

        <div className="relative">
          <div className="mb-2">
            <div className="bg-white rounded-xl px-3 py-1.5 inline-flex">
              <img src={applogo} alt="CuoreMD" className="h-8 w-auto" />
            </div>
          </div>
          <p className="text-blue-200 text-sm">Doctor Portal</p>
        </div>

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-white leading-tight mb-3">
              Welcome to CuoreMD™
            </h2>
            <p className="text-blue-200 text-base leading-relaxed max-w-sm mb-10">
              Smarter insights. Stronger decisions. Better outcomes.
            </p>
          </motion.div>

          <div className="space-y-5">
            {[
              {
                title: "Track Risk",
                desc: "Monitor cardiometabolic risk in real time.",
              },
              {
                title: "Boost Follow-up",
                desc: "Strengthen continuity and patient engagement.",
              },
              {
                title: "Red Flag Alerts",
                desc: "Act early with timely risk notifications.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.12, duration: 0.4 }}
                className="flex items-start gap-4"
              >
                <div className="w-2 h-2 rounded-full bg-blue-300 shrink-0 mt-2" />
                <div>
                  <p className="text-sm font-semibold text-white">
                    {item.title}
                  </p>
                  <p className="text-xs text-blue-200 mt-0.5 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="relative text-xs text-blue-300">
          CuoreMD™ — Supporting You Every Step of the Way.
        </p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-lg"
        >
          {/* Mobile logo */}
          <div className="mb-8 lg:hidden">
            <img src={applogo} alt="CuoreMD" className="h-7 w-auto" />
          </div>

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              Create Account
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Please fill in your details to register
            </p>
          </div>

          <div className="space-y-4">
            {/* Full Name + Mobile */}
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Full Name"
                placeholder="Dr"
                value={fullName}
                onChange={(e) => {
                  setFullName(e.target.value);
                  setFieldErrors((p) => ({ ...p, fullName: undefined }));
                }}
                icon={<User size={15} />}
                error={fieldErrors.fullName}
              />
              <Input
                label="Mobile Number"
                placeholder="10-digit number"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value.replace(/\D/g, "").slice(0, 10));
                  setFieldErrors((p) => ({ ...p, mobile: undefined }));
                }}
                icon={<Phone size={15} />}
                error={fieldErrors.mobile}
                maxLength={10}
              />
            </div>

            {/* Specialization dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Specialization
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setShowSpec(!showSpec);
                    setShowFee(false);
                  }}
                  className={`w-full h-10 px-3 rounded-xl border text-sm text-left flex items-center justify-between bg-white dark:bg-slate-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent ${fieldErrors.specialization ? "border-red-400" : "border-slate-200 dark:border-slate-600"}`}
                >
                  <span
                    className={
                      specialization
                        ? "text-slate-900 dark:text-slate-100"
                        : "text-slate-400 dark:text-slate-500"
                    }
                  >
                    {specialization || "Select your specialization"}
                  </span>
                  <ChevronDown
                    size={15}
                    className={`text-slate-500 dark:text-slate-400 transition-transform ${showSpec ? "rotate-180" : ""}`}
                  />
                </button>
                {showSpec && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                    {SPECIALIZATION_DETAILS.map((specialty) => (
                      <button
                        key={specialty.name}
                        type="button"
                        onClick={() => {
                          setSpecialization(specialty.name);
                          setShowSpec(false);
                          setFieldErrors((p) => ({
                            ...p,
                            specialization: undefined,
                          }));
                        }}
                        className={`w-full text-left px-4 py-3 border-b border-slate-50 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${specialization === specialty.name ? "bg-brand-50 dark:bg-brand-900/20" : ""}`}
                      >
                        <p
                          className={`text-sm font-semibold ${specialization === specialty.name ? "text-brand-700 dark:text-brand-400" : "text-slate-800 dark:text-slate-100"}`}
                        >
                          {specialty.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {specialty.description}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {fieldErrors.specialization && (
                <p className="text-xs text-red-500">
                  {fieldErrors.specialization}
                </p>
              )}
            </div>

            {/* Subscription Fee dropdown */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Subscription Fee (₹ / year)
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setShowFee(!showFee);
                    setShowSpec(false);
                  }}
                  className={`w-full h-10 px-3 rounded-xl border text-sm text-left flex items-center justify-between bg-white dark:bg-slate-800 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent ${fieldErrors.subscriptionFee ? "border-red-400" : "border-slate-200 dark:border-slate-600"}`}
                >
                  <span
                    className={
                      subscriptionFee
                        ? "text-slate-900 dark:text-slate-100"
                        : "text-slate-400 dark:text-slate-500"
                    }
                  >
                    {subscriptionFee
                      ? `₹${Number(subscriptionFee).toLocaleString("en-IN")} / year`
                      : "Select yearly fee"}
                  </span>
                  <ChevronDown
                    size={15}
                    className={`text-slate-500 dark:text-slate-400 transition-transform ${showFee ? "rotate-180" : ""}`}
                  />
                </button>
                {showFee && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-lg overflow-hidden">
                    {FEE_OPTIONS.map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => {
                          setSubscriptionFee(f);
                          setShowFee(false);
                          setFieldErrors((p) => ({
                            ...p,
                            subscriptionFee: undefined,
                          }));
                        }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${subscriptionFee === f ? "text-brand-700 dark:text-brand-400 font-semibold" : "text-slate-700 dark:text-slate-200"}`}
                      >
                        {subscriptionFee === f && (
                          <span className="mr-1.5">✓</span>
                        )}
                        ₹{Number(f).toLocaleString("en-IN")} / year
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {fieldErrors.subscriptionFee && (
                <p className="text-xs text-red-500">
                  {fieldErrors.subscriptionFee}
                </p>
              )}
            </div>

            {/* Address */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Address
              </label>
              <div className="grid grid-cols-3 gap-2">
                <Input
                  placeholder="Clinic address"
                  value={clinicAddress}
                  onChange={(e) => {
                    setClinicAddress(e.target.value);
                    setFieldErrors((p) => ({ ...p, clinicAddress: undefined }));
                  }}
                  icon={<Home size={13} />}
                  error={fieldErrors.clinicAddress}
                />
                <Input
                  placeholder="Area / Sector"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  icon={<MapPin size={13} />}
                />
                <Input
                  placeholder="City"
                  value={city}
                  onChange={(e) => {
                    setCity(e.target.value);
                    setFieldErrors((p) => ({ ...p, city: undefined }));
                  }}
                  icon={<Building2 size={13} />}
                  error={fieldErrors.city}
                />
              </div>
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Password"
                placeholder="Minimum 8 characters"
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFieldErrors((p) => ({ ...p, password: undefined }));
                }}
                icon={<Lock size={15} />}
                error={fieldErrors.password}
                iconRight={
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-600"
                  >
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                }
              />
              <Input
                label="Confirm Password"
                placeholder="Re-enter password"
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setFieldErrors((p) => ({ ...p, confirmPassword: undefined }));
                }}
                icon={<Lock size={15} />}
                error={fieldErrors.confirmPassword}
                iconRight={
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="text-slate-500 dark:text-slate-400 hover:text-slate-600"
                  >
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                }
              />
            </div>

            {/* Account Manager Code */}
            <Input
              label="Account Manager Code"
              placeholder="Enter AM Code"
              value={amCode}
              onChange={(e) => setAmCode(e.target.value)}
              icon={<Hash size={15} />}
            />

            {/* Terms */}
            <div className="flex flex-col gap-1">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => {
                    setAgreed(e.target.checked);
                    setFieldErrors((p) => ({ ...p, agreed: undefined }));
                  }}
                  className="mt-0.5 w-4 h-4 accent-brand-600 rounded"
                />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  I agree to the{" "}
                  <a
                    href="https://cuore.health/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:text-brand-700 font-medium"
                  >
                    Terms and Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://cuore.health/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:text-brand-700 font-medium"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
              {fieldErrors.agreed && (
                <p className="text-xs text-red-500">{fieldErrors.agreed}</p>
              )}
            </div>

            {/* OTP */}
            {otpSent && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="space-y-3"
              >
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Enter OTP
                </label>
                <div className="flex gap-3 justify-center">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={otpRefs[i]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="otp-input dark:bg-slate-800 dark:text-white"
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={loading}
                  className="w-full text-xs text-slate-500 hover:text-brand-600 py-1 transition-colors"
                >
                  Resend OTP
                </button>
              </motion.div>
            )}

            {/* Error */}
            {error && (
              <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-xl">
                {error}
              </p>
            )}

            {/* Submit */}
            <Button
              fullWidth
              loading={loading}
              onClick={otpSent ? handleRegister : handleSendOtp}
              icon={!loading && <ArrowRight size={16} />}
            >
              {otpSent ? "Register" : "Send OTP & Continue"}
            </Button>

            <p className="text-center text-sm text-slate-500 dark:text-slate-400">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-brand-600 hover:text-brand-700 font-medium"
              >
                Login here
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
